<script setup lang="ts">
import ArchetypePage from '~/components/archetypes/ArchetypePage.vue';

useSeoMeta({
  title: 'Defining the GMP Boundary for AI Outputs — QikSolve',
  description:
    'Understand when AI output affects a controlled record or quality decision, and what evidence and review that may require.',
});

const page = {
  hero: {
    eyebrow: 'AI output boundaries',
    title: 'Know when an AI output becomes part of controlled quality work',
    summary:
      'The AI governance response should follow what the output does in the process. A clear boundary helps teams decide what must be reviewed, evidenced, retained, and controlled.',
    cta: { label: 'Discuss your GMP AI pathway', to: '/contact' },
  },
  intro: {
    title: 'The tool does not set the boundary',
    content:
      'Process impact sets the boundary. An output used for private drafting has a different governance need from one that influences a quality decision or enters a controlled record.',
    items: [
      { title: 'Ask where the output goes', description: 'Map the next process step, decision, record, approval, release, or evidence set that may depend on it.' },
      { title: 'Keep the original context', description: 'Preserve enough source, output, reviewer, and version context to understand what happened.' },
      { title: 'Increase control with impact', description: 'As process impact increases, strengthen review, evidence, retention, escalation, and change-control expectations.' },
    ],
  },
  sections: [
    {
      title: 'Four planning classes',
      items: [
        { title: 'Informational support', description: 'Internal drafting, ideation, or non-controlled assistance with no direct decision authority.' },
        { title: 'Process guidance', description: 'Suggested steps or non-final summaries used to prepare work, with traceability and a named reviewer.' },
        { title: 'Decision-influencing output', description: 'Risk proposals, deviation support, or compliance-gap recommendations requiring accountable human decision ownership.' },
        { title: 'Record-integrated output', description: 'Output incorporated into a controlled record, approval, release, or evidence set, requiring an attributable evidence chain.' },
      ],
    },
    {
      title: 'When the boundary is uncertain',
      content:
        'Treat uncertainty as a governance question to resolve, not as permission to use the lower-control path. Confirm intended use, process impact, reviewer authority, evidence needs, and applicable GMP or computerised-system context before proceeding.',
      items: [
        { title: 'Define the accountable decision', description: 'Identify who can accept, reject, rework, or escalate the output.' },
        { title: 'Link changes to review', description: 'Consider whether changes to prompts, models, retrieval, rules, or tools alter the output boundary.' },
      ],
    },
  ],
  callout: {
    title: 'This is a planning model, not a universal classification',
    content: 'The right classification depends on intended use, process impact, jurisdiction, system boundary, and the customer quality system. Annex 11 may be relevant where the applicable computerised-system context requires it.',
    tone: 'boundary' as const,
  },
  relatedLinks: {
    title: 'Continue the decision',
    items: [
      { label: 'Practical, governed AI hub', description: 'Return to the full Pillar 5 governance pathway.', to: '/product/agentic-ai-governance' },
      { label: 'Human oversight patterns', description: 'Design review as a workflow rather than a signature.', to: '/product/human-oversight-for-gmp-ai-workflows' },
      { label: 'Regulatory compliance', description: 'Explore the wider regulatory and evidence context.', to: '/product/regulatory-compliance' },
    ],
  },
  cta: { title: 'Need to define an AI output boundary?', content: 'Bring the process and decision context to a practical governance conversation.', label: 'Discuss your pathway', to: '/contact' },
};
</script>

<template>
  <ArchetypePage v-bind="page" />
</template>
